import React from 'react';

/**
 * Button component with predefined variants matching the GiddyCarrot design system.
 * 
 * @param {string} variant - Button style variant
 *   - 'primary': Green background (default action)
 *   - 'secondary': Orange background (warning/alternative action)
 *   - 'ghost': Gray background (cancel/tertiary action)
 *   - 'danger': Red background (destructive action)
 *   - 'outline': White with green border
 * @param {string} size - Button size
 *   - 'sm': Smaller padding
 *   - 'md': Default size
 *   - 'lg': Larger padding
 * @param {boolean} fullWidth - Whether button takes full width
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Button content
 * @param {object} props - All other props passed to the button element
 * 
 * @example
 * <Button variant="primary" onClick={handleSave}>Save</Button>
 * <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
 * <Button variant="danger" size="sm">Delete</Button>
 */
const Button = ({ 
  variant = 'primary', 
  size = 'md',
  fullWidth = false,
  disabled = false,
  className = '',
  children, 
  ...props 
}) => {
  // Base classes
  const baseClasses = 'font-semibold rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-6 py-3',
    lg: 'px-8 py-4 text-lg',
  };
  
  // Variant classes
  const variantClasses = {
    primary: 'bg-[#52A675] hover:bg-[#4A9168] text-white focus:ring-[#52A675] disabled:bg-gray-300 disabled:cursor-not-allowed',
    secondary: 'bg-[#F4A261] hover:bg-[#E8956B] text-white focus:ring-[#F4A261] disabled:bg-gray-300 disabled:cursor-not-allowed',
    ghost: 'bg-gray-200 hover:bg-gray-300 text-[#2F5233] focus:ring-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed',
    danger: 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed',
    outline: 'bg-white border-2 border-[#52A675] text-[#52A675] hover:bg-[#52A675]/10 focus:ring-[#52A675] disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`.trim();
  
  return (
    <button
      className={combinedClasses}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Button group for modal footers - Save on left, Cancel on right.
 * 
 * @param {function} onSave - Save button handler
 * @param {function} onCancel - Cancel button handler
 * @param {string} saveText - Save button text (default: 'Save')
 * @param {string} cancelText - Cancel button text (default: 'Cancel')
 * @param {boolean} saveDisabled - Disable save button
 * @param {boolean} loading - Show loading state
 */
export const ModalButtonGroup = ({
  onSave,
  onCancel,
  saveText = 'Save',
  cancelText = 'Cancel',
  saveDisabled = false,
  loading = false,
}) => (
  <div className="flex gap-3">
    <Button 
      variant="primary" 
      onClick={onSave} 
      disabled={saveDisabled || loading}
    >
      {loading ? 'Saving...' : saveText}
    </Button>
    <Button 
      variant="ghost" 
      onClick={onCancel}
      disabled={loading}
    >
      {cancelText}
    </Button>
  </div>
);

/**
 * Icon button for actions like edit, delete, etc.
 * 
 * @param {React.ReactNode} icon - Icon element to render
 * @param {string} ariaLabel - Required accessible label
 * @param {string} variant - Color variant (default, danger, success)
 * @param {string} size - Button size (sm, md)
 * @param {object} props - All other props passed to the button element
 */
export const IconButton = ({ 
  icon, 
  ariaLabel, 
  variant = 'default',
  size = 'md',
  className = '',
  ...props 
}) => {
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
  };
  
  const variantClasses = {
    default: 'text-gray-400 hover:text-[#52A675] hover:bg-[#52A675]/10',
    danger: 'text-gray-400 hover:text-red-500 hover:bg-red-50',
    success: 'text-gray-400 hover:text-[#52A675] hover:bg-[#52A675]/10',
  };
  
  return (
    <button
      className={`${sizeClasses[size]} ${variantClasses[variant]} rounded-lg transition-colors ${className}`}
      aria-label={ariaLabel}
      {...props}
    >
      {icon}
    </button>
  );
};

export default Button;
